import { first, last } from 'lodash'

import DMMFStore from 'stores/dmmf/DMMFStore'
import ModelStore from 'stores/model/ModelStore'
import Model from 'stores/model/Model'
import FieldStore from 'stores/field/FieldStore'
import ScriptWhere from 'stores/script/ScriptWhere'
import { ISortOrder } from 'stores/script/ScriptSort'

import createError from 'utils/createError'
import dedup from 'utils/dedup'
import stringify from 'utils/stringify'

interface IParams {
  modelId: string
  where?: ScriptWhere
  fieldIds?: string[]
  sort?: {
    fieldId: string | null
    order: ISortOrder
  }
  pagination?: {
    take: number
    skip: number
  }
}

// Sorts field ids in the order that they appear in the Prisma schema
const sortFieldIds = (model: Model, fieldIds: string[]) => {
  return fieldIds.slice().sort((a: string, b: string) => {
    const aIndex = model.fieldIds.findIndex(fId => fId === a)
    const bIndex = model.fieldIds.findIndex(fId => fId === b)
    return aIndex - bIndex
  })
}

const getFindManyQuery = ({
  modelId,
  where,
  fieldIds,
  sort,
  pagination,
}: IParams) => {
  const script: string[] = []
  const model = ModelStore.get(modelId)
  if (!model) {
    throw createError('getFindManyQuery', 'Invalid modelId', {
      modelId,
    })
  }

  script.push(`prisma.${model.photonNamespace}.findMany({`)

  // `where` argument
  if (where && where?.size > 0) {
    script.push(`  where: {`)
    script.push(`    AND: [`)
    Object.values(where.values).forEach(w => {
      if (!w.enabled || !w.isValid) {
        return
      }
      // For this (Studio-only) operation, there's no need to look at `w.value`
      if (w.operation === 'isNull') {
        script.push(`      {`)
        if (first(w.fields)?.isRelation) {
          script.push(`       ${first(w.fields)?.name}: {`)
        }
        // Will always be a relation AND a list but separating for readability
        // List relations need to be wrapped with some:{}
        if (first(w.fields)?.isList) {
          script.push(`        some: {`)
        }
        script.push(`        ${last(w.fields)?.name}: { equals: null },`)
        if (first(w.fields)?.isList) {
          script.push(`      },`)
        }
        if (first(w.fields)?.isRelation) {
          script.push(`      },`)
        }
        script.push(`      },`)
      }

      // For this (Studio-only) operation, there's no need to look at `w.value`
      if (w.operation === 'isNotNull') {
        script.push(`      {`)
        if (first(w.fields)?.isRelation) {
          script.push(`       ${first(w.fields)?.name}: {`)
        }
        if (first(w.fields)?.isList) {
          script.push(`        some: {`)
        }
        script.push(
          `        ${last(w.fields)?.name}: { not: { equals: null } },`
        )
        if (first(w.fields)?.isList) {
          script.push(`      },`)
        }
        if (first(w.fields)?.isRelation) {
          script.push(`      },`)
        }
        script.push(`      },`)
      }

      if (!w.value) {
        return
      }

      script.push(`      {`)
      if (first(w.fields)?.isRelation) {
        script.push(`       ${first(w.fields)?.name}: {`)
      }
      if (first(w.fields)?.isList) {
        script.push(`        some: {`)
      }
      script.push(`        ${last(w.fields)?.name}: {`)

      if (['in', 'notIn'].includes(w.operation)) {
        // No need to try and stringify the value in this case, since it will already be an array
        script.push(`          ${w.operation}: ${w.value},`)
      } else {
        let whereValue = w.value
        if (last(w.fields)?.isDateTime && !last(w.fields)?.isList) {
          // Dates must be valid ISO
          whereValue = `"${new Date(whereValue).toISOString()}"`
        }
        if (
          (last(w.fields)?.isString || last(w.fields)?.isEnum) &&
          !whereValue.startsWith('"')
        ) {
          // If there isn't already a " at the beginning of the string, add one
          whereValue = '"'.concat(whereValue)
        }
        if (
          (last(w.fields)?.isString || last(w.fields)?.isEnum) &&
          !whereValue.endsWith('"')
        ) {
          // If there isn't already a " at the end of the string, add one
          whereValue = whereValue.concat('"')
        }
        if (last(w.fields)?.isBigInt) {
          // For BigInts, construct a BigInt
          whereValue = stringify(BigInt(whereValue))
        }
        script.push(`          ${w.operation}: ${whereValue},`)
        if (last(w.fields)?.isString && DMMFStore.isPostgresDatasource) {
          // For strings in Postgres, search case insensitively
          script.push(`          mode: "insensitive",`)
        }
      }
      script.push(`        },`)
      if (first(w.fields)?.isList) {
        script.push(`        },`)
      }
      if (first(w.fields)?.isRelation) {
        script.push(`       },`)
      }
      script.push(`      },`)
    })
    script.push(`    ]`)
    script.push(`  },`)
  }

  // `orderBy` argument
  if (sort?.fieldId && sort?.order) {
    const sortField = FieldStore.get(sort.fieldId)
    if (!sortField) {
      throw createError('getFindManyQuery', 'Invalid sort fieldId', {
        sort,
      })
    }
    script.push(`  orderBy: [{`)
    script.push(`    ${sortField.name}: "${sort.order}",`)
    script.push(`  }],`)
  }

  // `take` argument
  if (pagination?.take !== undefined && pagination.take !== null) {
    script.push(`  take: ${Number(pagination.take)},`)
  }

  // `skip` argument
  if (pagination?.skip !== undefined && pagination.skip !== null) {
    script.push(`  skip: ${Number(pagination.skip)},`)
  }

  // `select` argument
  if (!fieldIds) {
    fieldIds = model.fieldIds
  } else {
    fieldIds = dedup(
      ([] as string[]).concat(
        model.uniqueIdentifiers.map(f => f.id), // Always `select` id fields so that returned records' recordId can be calculated correctly
        fieldIds
      )
    )
  }
  const fields = sortFieldIds(model, fieldIds).map(fId => FieldStore.get(fId))
  script.push(`  select: {`)
  fields.forEach(f => {
    if (!f) {
      return
    }
    script.push(`    ${f.name}: true,`)
  })

  script.push(`  }`)
  script.push(`})`)
  return script.join('\n')
}

export default getFindManyQuery
